import React from "react";
import { Helmet } from "react-helmet";
import { Key, CheckCircle, AlertTriangle, FileText } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/seed-phrase-recovery" className="dark-nav-link active">SEED RECOVERY</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const SeedPhraseRecoveryPage = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Seed Phrase Recovery Service | Missing Words, Typos, Wrong Order</title>
        <meta name="description" content="Professional BIP39 seed phrase recovery for 1-5 missing words, typos, and wrong order. Non-custodial, UK-based. Free assessment." />
        <link rel="canonical" href="https://arcana-crypto.com/seed-recovery-seo.html" />
      </Helmet>
      <Navigation />
      
      <section className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title" style={{fontSize: '48px'}}>
            Seed Phrase Recovery Service
          </h1>
          <p className="section-subtitle">
            Professional BIP39 Seed Phrase Recovery (1-5 Missing Words)
          </p>

          <div className="section-intro" style={{marginTop: '40px'}}>
            <p style={{fontSize: '20px'}}>
              Lost or incomplete seed phrases are one of the most common causes of cryptocurrency 
              inaccessibility. Arcana Crypto specializes in professional seed phrase recovery using 
              controlled computational methodologies and proprietary tools.
            </p>
          </div>

          {/* What We Recover */}
          <div style={{marginTop: '80px'}}>
            <h2 className="section-title">What We Recover</h2>
            
            <div className="card-grid" style={{marginTop: '60px'}}>
              <div className="card">
                <Key className="card-icon" />
                <h3 className="card-title">MISSING WORDS</h3>
                <ul className="card-list">
                  <li>1 missing word: £300</li>
                  <li>2 missing words: £600</li>
                  <li>3 missing words: £1,200</li>
                  <li>4 missing words: £3,000</li>
                  <li>5 missing words: £2,000 + 15% success fee</li>
                </ul>
                <div className="card-price">Success Rate: 70-95%</div>
              </div>

              <div className="card">
                <CheckCircle className="card-icon" />
                <h3 className="card-title">INCORRECT ORDER</h3>
                <ul className="card-list">
                  <li>12-word permutation: £800</li>
                  <li>24-word partial permutation: £2,000</li>
                  <li>Smart algorithm optimization</li>
                  <li>Time: 30 minutes - 2 hours</li>
                </ul>
                <div className="card-price">Success Rate: High</div>
              </div>

              <div className="card">
                <AlertTriangle className="card-icon" />
                <h3 className="card-title">TYPOS & ERRORS</h3>
                <ul className="card-list">
                  <li>Visual confusion (b/d, 1/l, etc.)</li>
                  <li>Spelling mistakes (1-2 words)</li>
                  <li>Similar-sounding words</li>
                  <li>Price: £400</li>
                </ul>
                <div className="card-price">Success Rate: 90%+</div>
              </div>

              <div className="card">
                <FileText className="card-icon" />
                <h3 className="card-title">COMBINED ISSUES</h3>
                <ul className="card-list">
                  <li>Missing words + wrong order</li>
                  <li>Typos + missing words</li>
                  <li>Custom pricing based on complexity</li>
                  <li>Free assessment first</li>
                </ul>
                <div className="card-price">Custom Quote</div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">How Seed Phrase Recovery Works</h2>
            
            <div className="steps-grid" style={{marginTop: '60px'}}>
              <div className="step">
                <div className="step-number">01</div>
                <h3 className="step-title">PROVIDE DETAILS</h3>
                <p className="step-description">
                  Tell us how many words are missing, incorrect order, or contain typos
                </p>
              </div>

              <div className="step">
                <div className="step-number">02</div>
                <h3 className="step-title">TECHNICAL ASSESSMENT</h3>
                <p className="step-description">
                  We evaluate probability, computational requirements, and provide transparent quote
                </p>
              </div>

              <div className="step">
                <div className="step-number">03</div>
                <h3 className="step-title">CONTROLLED RECOVERY</h3>
                <p className="step-description">
                  Using proprietary tools and GPUs-accelerated computation (where needed)
                </p>
              </div>

              <div className="step">
                <div className="step-number">04</div>
                <h3 className="step-title">SECURE DELIVERY</h3>
                <p className="step-description">
                  Complete seed phrase delivered encrypted - you verify and gain access
                </p>
              </div>
            </div>
          </div>

          {/* Supported Wallets */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">Supported Wallet Types</h2>
            
            <div className="two-column" style={{marginTop: '60px'}}>
              <div>
                <h3 className="column-title">SOFTWARE WALLETS</h3>
                <ul className="column-list">
                  <li>Electrum, Exodus, Trust Wallet</li>
                  <li>MetaMask, MyEtherWallet</li>
                  <li>Atomic Wallet, Coinomi</li>
                  <li>Any BIP39-compliant wallet</li>
                </ul>
              </div>

              <div>
                <h3 className="column-title">HARDWARE WALLETS</h3>
                <ul className="column-list">
                  <li>Ledger (seed phrase recovery only)</li>
                  <li>Trezor (seed phrase recovery only)</li>
                  <li>KeepKey, SafePal</li>
                  <li>All BIP39 hardware wallets</li>
                </ul>
              </div>
            </div>

            <p className="section-intro" style={{marginTop: '40px', fontStyle: 'italic', fontSize: '14px'}}>
              Note: We recover seed phrases, not hardware device PINs. Seed phrase recovery works 
              independently of the physical device.
            </p>
          </div>

          {/* CTA */}
          <div style={{textAlign: 'center', marginTop: '100px'}}>
            <h2 className="cta-title" style={{fontSize: '42px'}}>
              Lost Your Seed Phrase?
            </h2>
            <p className="section-intro" style={{marginBottom: '40px'}}>
              Free technical assessment. No obligation. Transparent pricing.
            </p>
            <button className="btn-primary" style={{fontSize: '20px', padding: '20px 48px'}} onClick={() => window.location.href = '/#assessment'}>
              START FREE ASSESSMENT
            </button>
          </div>

        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeedPhraseRecoveryPage;
